<template>
  <div :class="bem.b()">
    <a-spin :spinning="props.loading">
      <a-form
        v-bind="computedFormOptions"
        :id="id"
        ref="formRef"
        :model="props.formData"
      >
        <a-row v-bind="props.rowOptions">
          <a-col
            v-for="(item, index) in computedFormColumns"
            :key="index"
            v-bind="item?.colOptions ?? {}"
            :span="item?.spanCol ?? 24"
          >
            <slot name="formItem" :item="item" :index="index">
              <ll-form-item
                v-bind="getBindValue(item)"
                :name="item?.key ?? index"
                :class="[getFormItemClass(item), item.class]"
              >
                <template #label>
                  <slot name="label" :item="item" :index="index">
                    <span>{{ item.label }}</span>
                    <span v-if="item?.tip" :class="bem.e('tooltip-wrapper')">
                      <ll-tooltip
                        style="margin-left: 8px"
                        :overlay-class-name="bem.e('tooltip')"
                        :arrow-point-at-center="true"
                        :max-width="item?.tipConfig?.maxWidth ?? 400"
                        :placement="item?.tipConfig?.placement ?? 'top'"
                        :tip="item.tip"
                        v-bind="item?.tipConfig"
                      />
                    </span>
                    <span v-if="item?.info" :class="bem.e('tooltip-wrapper')">
                      <ll-tooltip
                        style="margin-left: 8px"
                        :overlay-class-name="bem.e('tooltip')"
                        :arrow-point-at-center="true"
                        :max-width="item?.tipConfig?.maxWidth ?? 400"
                        :placement="item?.tipConfig?.placement ?? 'top'"
                        :tip="item.info"
                        v-bind="item?.tipConfig"
                      >
                        <ll-icon icon-name="icon-information" :size="14" />
                      </ll-tooltip>
                    </span>
                  </slot>
                </template>
                <slot name="content" :item="item" :index="index">
                  <template v-if="!isEmpty(item.children)">
                    <a-row :gutter="5" v-bind="item.rowOptions">
                      <a-col
                        v-for="(child, childIndex) in item.children"
                        :key="childIndex"
                        :style="{
                          display: 'flex',
                          'align-items': 'center',
                        }"
                        v-bind="item?.colOptions ?? {}"
                      >
                        <slot name="children" :item="child" :index="index">
                          <form-component
                            :item="child"
                            :form-data="formData"
                            @change-field="handleChangeField"
                            @blur-field="handleBlurField"
                          />
                        </slot>
                      </a-col>
                    </a-row>
                  </template>
                  <form-component
                    v-else
                    :ref="(el) => (formComponents[index] = el)"
                    :item="item"
                    :form-data="formData"
                    @change-field="handleChangeField"
                    @blur-field="handleBlurField"
                  />
                </slot>
                <template v-if="item.extra" #extra>
                  <slot name="extra" :item="item" :index="index">
                    {{ item.extra }}
                  </slot>
                </template>
              </ll-form-item>
            </slot>
          </a-col>
        </a-row>
      </a-form>
    </a-spin>
  </div>
</template>
<script setup lang="ts">
import { has, isEmpty, omit } from 'lodash-es'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'
import { formEmits, formProps, type FormColumn } from './config'

import type { NamePath } from 'ant-design-vue/es/form/interface'

import { createNamespace, deepUnref } from '@ll-plus/utils'

import formComponent from './components/form-component.vue'

// 定义组件名称
defineOptions({ name: 'LlForm' })

// Props
const props = defineProps(formProps)

const emits = defineEmits(formEmits)

const bem = createNamespace('form')

const formData = reactive(props.formData)

const formRef = ref()

const formComponents = ref<InstanceType<typeof formComponent>[]>(
  [] as InstanceType<typeof formComponent>[]
)

const computedFormColumns = computed(() => {
  const formColumns = deepUnref(props.formColumns) as FormColumn[]
  return formColumns.map((e) => {
    e.disabled = has(e, 'disabled') ? e.disabled : props.disabled
    if (e.children && e.children.length) {
      e.children.map((a) => {
        a.disabled = has(a, 'disabled') ? a.disabled : props.disabled
        return a
      })
    }
    return e
  })
})

const validateFields = computed(() => {
  return props.formColumns
    .filter((e) => e.required || (has(e, 'rules') && e.rules.length > 0))
    .map((e) => {
      return e.key
    })
})

const computedFormOptions = computed(() => {
  return {
    scrollToFirstError: !props.validateErrorStop,
    ...props.formOptions,
  }
})

const id = ref(`ll-form-${+new Date()}`)

const validate = async (nameList?: NamePath[]) => {
  if (props.validateErrorStop) {
    clearValidate()
    if (!validateFields.value.length) return Promise.resolve()
    for (const i of validateFields.value) {
      try {
        await formRef.value.validateFields([i])
      } catch (error) {
        return Promise.reject(error) // 跳出验证循环并返回错误
      }
    }
    return Promise.resolve()
  } else {
    try {
      await formRef.value.validateFields(nameList || validateFields.value)
      return Promise.resolve()
    } catch (error) {
      return Promise.reject(error)
    }
  }
}

const resetFields = async () => {
  await formRef.value.resetFields()
}

const clearValidate = async (nameList?: NamePath[]) => {
  await formRef.value.clearValidate(nameList)
}

const scrollToField = async (name: NamePath, options?: ScrollOptions) => {
  await formRef.value.scrollToField(name, options)
}

const getBindValue = (item: FormColumn) => {
  const obj = { ...item }
  return omit(obj, ['label', 'class', 'extra'])
}

const getFormItemClass = (item = {} as FormColumn) => {
  if (props.formOptions.layout === 'horizontal') {
    if (item?.labelCol) {
      return item.labelCol.span && item.labelCol.span >= 24
        ? bem.is('label-top')
        : bem.is('label-left')
    } else {
      return bem.is('label-left')
    }
  }
  return bem.is('label-top')
}

const handleChangeField = (item: FormColumn) => {
  emits('change', { item, value: formData[item.key], formData })
}
const handleBlurField = (item: FormColumn) => {
  emits('blur', { item, value: formData[item.key], formData })
}

watch(
  () => props.formData,
  async (val) => {
    for (const i in val) {
      formData[i] = val[i]
    }
  },
  {
    deep: true,
    immediate: true,
  }
)

onMounted(async () => {
  await nextTick()
  if (props.autoFocus) {
    for (const element of formComponents.value) {
      const item = element?.getItem()
      if (['input', 'textarea', 'input-number'].includes(item.type)) {
        element?.focus()
        break
      }
    }
  }
})

defineExpose({
  validate,
  resetFields,
  clearValidate,
  scrollToField,
})
</script>
